<template>
    <div class="emission-calc-result-panel-compact">
        <div class="filter-section-compact">
            <div class="section-header-compact">
                <div class="filter-right-group">
                    <div class="filter-fields-group">
                        <div class="mode-toggle">
                            <button
                                type="button"
                                class="mode-btn"
                                :class="{ active: mode === 'monthly' }"
                                @click="setMode('monthly')"
                            >
                                월별
                            </button>
                            <button
                                type="button"
                                class="mode-btn"
                                :class="{ active: mode === 'annual' }"
                                @click="setMode('annual')"
                            >
                                연도별
                            </button>
                        </div>

                        <CarbonSelect
                            :value="filters.orgIdx == null ? '' : filters.orgIdx"
                            :options="orgSelectOptions"
                            placeholder="조직 선택(필수)"
                            :placeholderDisabled="true"
                            @input="
                                (v) => {
                                    filters.orgIdx = v === '' ? null : Number(v);
                                }
                            "
                        />

                        <DatePicker
                            v-if="mode === 'monthly'"
                            v-model="periodFromMonth"
                            type="month"
                            format="YYYY-MM"
                            value-type="format"
                            placeholder="시작월"
                            class="period-picker"
                        />
                        <DatePicker
                            v-if="mode === 'monthly'"
                            v-model="periodToMonth"
                            type="month"
                            format="YYYY-MM"
                            value-type="format"
                            placeholder="종료월"
                            class="period-picker"
                        />

                        <DatePicker
                            v-if="mode === 'annual'"
                            v-model="periodFromYear"
                            type="year"
                            format="YYYY"
                            value-type="format"
                            placeholder="시작연도"
                            class="period-picker"
                        />
                        <DatePicker
                            v-if="mode === 'annual'"
                            v-model="periodToYear"
                            type="year"
                            format="YYYY"
                            value-type="format"
                            placeholder="종료연도"
                            class="period-picker"
                        />

                        <CarbonSelect
                            :value="filters.facilityCode"
                            :options="facilitySelectOptions"
                            :placeholder="loadingFacilities ? '시설 불러오는 중…' : '전체 시설'"
                            :placeholderDisabled="false"
                            :disabled="!filters.orgIdx || loadingFacilities"
                            @input="(v) => (filters.facilityCode = v === '' ? '' : String(v))"
                        />

                        <CarbonSelect
                            :value="filters.categoryCode"
                            :options="categorySelectOptions"
                            :placeholder="loadingCategories ? '카테고리 불러오는 중…' : '전체 카테고리'"
                            :placeholderDisabled="false"
                            :disabled="loadingCategories"
                            @input="(v) => (filters.categoryCode = v === '' ? '' : String(v))"
                        />

                        <CarbonButton icon="fas fa-redo" @click="resetFilters">초기화</CarbonButton>
                        <CarbonButton variant="primary" icon="fas fa-search" @click="loadLines">조회</CarbonButton>
                    </div>
                </div>
            </div>
        </div>

        <EmissionLinesTable
            :items="paginatedItems"
            :loading="loading"
            :facility-name-map="facilityNameMap"
            :category-name-map="categoryNameMap"
        />

        <TablePagination
            v-if="!loading && (items || []).length > 0"
            :total-items="(items || []).length"
            :current-page="pagination.page"
            :page-size="pagination.limit"
            :page-size-options="[10, 20, 50, 100, 200]"
            @update:currentPage="onPageChange"
            @update:pageSize="onPageSizeChange"
        />
    </div>
</template>

<script>
import { fetchAnnualInventoryLines, fetchInventoryLines } from "../../services/inventoryService.js";
import EmissionLinesTable from "./EmissionLinesTable.vue";
import { fetchOrganizations } from "../inventory/services/calcRunnerService.js";
import carbonApi from "@/submodules/carbon-plugin/api";
import { unwrapResponse, extractArrayData, extractErrorMessage } from "@/submodules/carbon-plugin/utils/api.js";
import TablePagination from "../common/TablePagination.vue";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import CarbonSelect from "../common/CarbonSelect.vue";
import CarbonButton from "../common/CarbonButton.vue";
import excelHelper from "@/libs/excelHelper.js";
import CarbonCodes from "@/submodules/carbon-plugin/codes";

export default {
    name: "EmissionCalcResultPanel",
    components: {
        EmissionLinesTable,
        DatePicker,
        CarbonSelect,
        CarbonButton,
        TablePagination,
    },
    props: {
        loginUser: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            apiUnit: "kgCO2e",
            displayUnit: "kgCO2e",

            mode: "monthly", // monthly | annual
            items: [],
            pagination: {
                page: 1,
                limit: 10,
            },

            loading: false,
            loadingFacilities: false,
            loadingCategories: false,

            filters: {
                orgIdx: null, // 필수
                facilityCode: "", // '' = 전체
                categoryCode: "", // '' = 전체
            },

            organizations: [],
            facilities: [],
            categories: [],

            periodFromMonth: "",
            periodToMonth: "",
            periodFromYear: "",
            periodToYear: "",
        };
    },
    computed: {
        orgSelectOptions() {
            const list = Array.isArray(this.organizations) ? this.organizations : [];
            return list
                .map((o) => {
                    const value = o.orgIdx ?? o.orgCode ?? null;
                    if (value == null) return null;
                    const label = o.orgName ?? o.name ?? `조직 #${value}`;
                    return { value, text: label };
                })
                .filter(Boolean);
        },
        facilitySelectOptions() {
            const list = Array.isArray(this.facilities) ? this.facilities : [];
            return list
                .map((f) => {
                    const code = f.facilityCode ?? null;
                    if (!code) return null;
                    const name = f.facilityName ?? f.name ?? "";
                    const label = name ?? String(code);
                    return { value: String(code), text: label };
                })
                .filter(Boolean);
        },
        categorySelectOptions() {
            const list = Array.isArray(this.categories) ? this.categories : [];
            return list
                .map((c) => {
                    const code = c.categoryCode ?? null;
                    if (!code) return null;
                    const name = c.categoryName ?? c.name ?? "";
                    const label = name ?? String(code);
                    return { value: String(code), text: label };
                })
                .filter(Boolean);
        },
        facilityNameMap() {
            const map = {};
            const list = Array.isArray(this.facilities) ? this.facilities : [];
            list.forEach((f) => {
                const code = f?.facilityCode;
                if (!code) return;
                const name = f?.facilityName ?? f?.name ?? null;
                if (!name) return;
                map[String(code)] = name;
            });
            return map;
        },
        categoryNameMap() {
            const map = {};
            const list = Array.isArray(this.categories) ? this.categories : [];
            list.forEach((c) => {
                const code = c?.categoryCode;
                if (!code) return;
                const name = c?.categoryName ?? c?.name ?? "";
                map[String(code)] = name;
            });
            return map;
        },
        paginatedItems() {
            const list = Array.isArray(this.items) ? this.items : [];
            const page = Number(this.pagination.page) || 1;
            const limit = Number(this.pagination.limit) || 20;
            const start = (page - 1) * limit;
            return list.slice(start, start + limit);
        },
    },
    mounted() {
        this.loadOrganizations();
        this.loadCategories();
    },
    watch: {
        "filters.orgIdx": {
            async handler(newVal, oldVal) {
                if (newVal === oldVal) return;
                // org 변경 시: 기존 시설 선택/목록 정리, 테이블 데이터 클리어
                this.filters.facilityCode = "";
                this.facilities = [];
                this.items = [];
                this.pagination.page = 1;
                if (!newVal) return;
                await this.loadFacilities();
            },
        },
        items: {
            handler(val) {
                this.$emit("items-changed", Array.isArray(val) && val.length > 0);
            },
            immediate: true,
        },
    },
    methods: {
        async loadOrganizations() {
            try {
                const orgs = await fetchOrganizations();
                const list = Array.isArray(orgs) ? orgs : [];
                this.organizations = list;
                if (this.filters.orgIdx == null && list.length > 0) {
                    const first = list[0];
                    const v = first.orgIdx ?? first.orgCode ?? null;
                    if (v != null) this.filters.orgIdx = Number(v);
                }
                if (this.filters.orgIdx != null) {
                    await this.loadFacilities();
                    // 요구사항: 최초 마운트 시 현재 초기값으로 1회 조회
                    await this.loadLines();
                }
            } catch (e) {
                console.error("조직 목록 로드 실패:", e);
                this.organizations = [];
            }
        },
        async loadFacilities() {
            if (!this.filters.orgIdx) return;
            if (this.loadingFacilities) return;
            this.loadingFacilities = true;
            try {
                // 요구사항: 시설 목록 조회는 orgIdx만 사용
                const res = await carbonApi.utility.getFacilities(null, { orgIdx: Number(this.filters.orgIdx) });
                const data = unwrapResponse(res, { defaultMessage: "시설 목록을 불러오지 못했습니다." });
                this.facilities = extractArrayData(data, ["facilities", "items", "data"]);
            } catch (e) {
                console.error("시설 목록 로드 실패:", e);
                this.facilities = [];
                this.$bvToast?.toast?.(extractErrorMessage(e, "시설 목록을 불러오지 못했습니다."), {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loadingFacilities = false;
            }
        },
        async loadCategories() {
            if (this.loadingCategories) return;
            this.loadingCategories = true;
            try {
                // 요구사항: getAllCategories 사용
                const res = await carbonApi.operBoundary.getAllCategories();
                const data = unwrapResponse(res, { defaultMessage: "카테고리 목록을 불러오지 못했습니다." });
                this.categories = extractArrayData(data, ["categories", "items", "data"]);
            } catch (e) {
                console.error("카테고리 목록 로드 실패:", e);
                this.categories = [];
                this.$bvToast?.toast?.(extractErrorMessage(e, "카테고리 목록을 불러오지 못했습니다."), {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loadingCategories = false;
            }
        },
        resetFilters() {
            this.filters.facilityCode = "";
            this.filters.categoryCode = "";
            this.periodFromMonth = "";
            this.periodToMonth = "";
            this.periodFromYear = "";
            this.periodToYear = "";
            this.items = [];
            this.pagination.page = 1;
        },
        setMode(next) {
            if (next === this.mode) return;
            this.mode = next;
            this.items = [];
            this.pagination.page = 1;
        },
        buildQueryParams() {
            if (!this.filters.orgIdx) {
                throw new Error("조직(orgIdx)은 필수입니다.");
            }
            const params = {
                orgIdx: Number(this.filters.orgIdx),
                facilityCode: this.filters.facilityCode || null,
                categoryCode: this.filters.categoryCode || null,
            };
            if (this.mode === "monthly") {
                const from = this.periodFromMonth ? String(this.periodFromMonth).replace(/-/g, "") : null;
                const to = this.periodToMonth ? String(this.periodToMonth).replace(/-/g, "") : null;
                if (from) params.periodFrom = from;
                if (to) params.periodTo = to;
            } else {
                if (this.periodFromYear) params.periodFrom = String(this.periodFromYear);
                if (this.periodToYear) params.periodTo = String(this.periodToYear);
            }
            return params;
        },
        async loadLines() {
            if (this.loading) return;
            this.loading = true;
            try {
                const params = this.buildQueryParams();
                const res =
                    this.mode === "monthly"
                        ? await fetchInventoryLines(params)
                        : await fetchAnnualInventoryLines(params);
                this.items = Array.isArray(res?.items) ? res.items : [];
                this.pagination.page = 1;
            } catch (e) {
                console.error("라인 목록 조회 실패:", e);
                this.items = [];
                const msg = e?.response?.data?.message || e?.message || "목록 조회에 실패했습니다.";
                this.$bvToast?.toast?.(msg, { variant: "danger", title: "오류" });
            } finally {
                this.loading = false;
            }
        },
        onPageChange(page) {
            const totalPages = Math.ceil((this.items?.length || 0) / (this.pagination.limit || 20)) || 1;
            this.pagination.page = Math.max(1, Math.min(Number(page) || 1, totalPages));
        },
        onPageSizeChange(size) {
            this.pagination.limit = Number(size) || 20;
            this.pagination.page = 1;
        },
        formatTimeKeyForExport(row) {
            const v = row?.timeKey;
            if (!v) return "-";
            const s = String(v);
            if (row?.mode === "annual") return s;
            if (s.length === 6) return `${s.slice(0, 4)}.${s.slice(4, 6)}`;
            return s;
        },
        formatScopeForExport(scope) {
            if (scope == null) return "-";
            const m = String(scope).match(/([123])/);
            return m ? `S${m[1]}` : String(scope);
        },
        buildExcelData() {
            const list = Array.isArray(this.items) ? this.items : [];
            const map = this.facilityNameMap || {};
            return list.map((row) => {
                const facilityCode = row.facilityCode ?? "";
                const facilityName = map[facilityCode] ?? "";
                const categoryCode = row.categoryCode ?? "";
                const categoryName = this.categoryNameMap?.[categoryCode] ?? "";
                const categoryDisplay = categoryName ? `${categoryName} (${categoryCode})` : categoryCode || "-";
                const statLabel = CarbonCodes.inventoryStat?.getLabel?.(row.inventoryStat) ?? row.inventoryStat ?? "-";
                return {
                    기간: this.formatTimeKeyForExport(row),
                    시설코드: facilityCode || "-",
                    시설명: facilityName || "-",
                    카테고리: categoryDisplay,
                    Scope: this.formatScopeForExport(row.scope),
                    활동수: row.activityCount != null ? Number(row.activityCount) : null,
                    CO2: row.CO2,
                    CH4: row.CH4,
                    N2O: row.N2O,
                    HFCs: row.HFCs,
                    PFCs: row.PFCs,
                    SF6: row.SF6,
                    CO2eqCO2: row.CO2eqCO2,
                    CO2eqCH4: row.CO2eqCH4,
                    CO2eqN2O: row.CO2eqN2O,
                    CO2eqHFCs: row.CO2eqHFCs,
                    CO2eqPFCs: row.CO2eqPFCs,
                    CO2eqSF6: row.CO2eqSF6,
                    CO2eqTotal: row.co2eqTotal,
                    상태: statLabel,
                };
            });
        },
        async exportExcel() {
            const list = Array.isArray(this.items) ? this.items : [];
            if (!list.length) {
                this.$bvToast?.toast?.("다운로드할 데이터가 없습니다.", { variant: "warning", title: "알림" });
                return;
            }
            try {
                const columnDefs = [
                    { key: "기간", header: "기간", width: 10 },
                    { key: "시설코드", header: "시설코드", width: 12 },
                    { key: "시설명", header: "시설명", width: 18 },
                    { key: "카테고리", header: "카테고리", width: 15 },
                    { key: "Scope", header: "Scope", width: 8 },
                    { key: "활동수", header: "활동수", width: 10 },
                    { key: "CO2", header: "CO2", group: "온실가스 배출량 (kgGHG)", width: 12 },
                    { key: "CH4", header: "CH4", group: "온실가스 배출량 (kgGHG)", width: 12 },
                    { key: "N2O", header: "N2O", group: "온실가스 배출량 (kgGHG)", width: 12 },
                    { key: "HFCs", header: "HFCs", group: "온실가스 배출량 (kgGHG)", width: 12 },
                    { key: "PFCs", header: "PFCs", group: "온실가스 배출량 (kgGHG)", width: 12 },
                    { key: "SF6", header: "SF6", group: "온실가스 배출량 (kgGHG)", width: 12 },
                    { key: "CO2eqCO2", header: "CO2", group: "CO2 환산량 (kgCO2eq)", width: 12 },
                    { key: "CO2eqCH4", header: "CH4", group: "CO2 환산량 (kgCO2eq)", width: 12 },
                    { key: "CO2eqN2O", header: "N2O", group: "CO2 환산량 (kgCO2eq)", width: 12 },
                    { key: "CO2eqHFCs", header: "HFCs", group: "CO2 환산량 (kgCO2eq)", width: 12 },
                    { key: "CO2eqPFCs", header: "PFCs", group: "CO2 환산량 (kgCO2eq)", width: 12 },
                    { key: "CO2eqSF6", header: "SF6", group: "CO2 환산량 (kgCO2eq)", width: 12 },
                    { key: "CO2eqTotal", header: "배출량(kgCO2eq)", width: 12 },
                    { key: "상태", header: "상태", width: 10 },
                ];
                const numKeys = [
                    "활동수",
                    "CO2",
                    "CH4",
                    "N2O",
                    "HFCs",
                    "PFCs",
                    "SF6",
                    "CO2eqCO2",
                    "CO2eqCH4",
                    "CO2eqN2O",
                    "CO2eqHFCs",
                    "CO2eqPFCs",
                    "CO2eqSF6",
                    "CO2eqTotal",
                ];
                const cellTypes = Object.fromEntries(numKeys.map((h) => [h, "number"]));
                const data = this.buildExcelData();
                const modeLabel = this.mode === "monthly" ? "월별" : "연도별";
                const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
                const org = this.organizations?.find((o) => (o.orgIdx ?? o.orgCode) == this.filters.orgIdx);
                const orgName = (org?.orgName ?? org?.name ?? "").replace(/[/\\:*?"<>|]/g, "_").trim() || "전체";
                const fileName = `${orgName}_${modeLabel}_배출량 산정 내역_${dateStr}.xlsx`;
                await excelHelper.createAndDownloadWorkbookWithGroupedHeaders(data, columnDefs, fileName, {
                    sheetName: "배출량 산정 내역",
                    cellTypes,
                    alternatingColors: true,
                    freezeHeader: true,
                    autoFilter: true,
                    autoFilterColumns: ["시설코드", "시설명", "카테고리", "Scope"],
                });
                this.$bvToast?.toast?.(`엑셀 파일이 다운로드되었습니다. (${list.length}건)`, {
                    variant: "success",
                    title: "완료",
                });
            } catch (e) {
                console.error("엑셀 다운로드 실패:", e);
                this.$bvToast?.toast?.(e?.message || "엑셀 다운로드에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
    },
};
</script>

<style scoped>
.emission-calc-result-panel-compact {
    padding: 10px 16px;
    height: 100%;
    min-height: 200px;
}

.section-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.filter-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
    flex-shrink: 0;
}

.filter-right-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-left: auto;
}

.filter-fields-group {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap; /* 요구사항: 검색 영역 한줄 고정 */
    align-items: center;
    overflow-x: auto; /* 한줄 유지 + 폭 초과 시 가로 스크롤 */
    overflow-y: hidden;
}

.filter-fields-group > * {
    flex: 0 0 auto;
}

.filter-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}

.mx-datepicker {
    width: 210px !important;
}

.period-picker {
    width: 160px;
}

.mode-toggle {
    display: inline-flex;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
}

.mode-btn {
    padding: 8px 12px;
    border: none;
    background: transparent;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
}

.mode-btn.active {
    background: #52b788;
    color: #fff;
}
</style>
